import { Head, router, usePage } from "@inertiajs/react"
import { PenLine } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/app-layout"
import { newStoryPath, storiesPath } from "@/routes"
import type { BreadcrumbItem, SharedProps, Story } from "@/types"

import StoryCard from "./story-card"

interface Props {
  stories: Story[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Newsroom", href: storiesPath() },
]

export default function StoriesIndex({ stories }: Props) {
  const { platforms, story_types } = usePage<SharedProps>().props
  const platformLabels = Object.fromEntries(
    platforms.map(({ name, label }) => [name, label]),
  )

  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get("platform"),
  )

  function handlePlatformChange(value: string) {
    const platform = value === "all" ? null : value
    setSelectedPlatform(platform)

    const url = new URL(window.location.href)
    if (platform) {
      url.searchParams.set("platform", platform)
    } else {
      url.searchParams.delete("platform")
    }
    window.history.replaceState({}, "", url.toString())
  }

  const filteredStories = selectedPlatform
    ? stories.filter((story) =>
        story.syndications.some((s) => s.platform.name === selectedPlatform),
      )
    : stories

  const drafts = filteredStories.filter((story) => story.status === "draft")
  const scheduled = filteredStories.filter(
    (story) => story.status === "scheduled",
  )
  const published = filteredStories.filter(
    (story) => story.status === "published",
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Newsroom" />

      <div className="flex flex-col gap-8 p-6">
        <div className="flex items-center justify-between">
          <Select
            value={selectedPlatform ?? "all"}
            onValueChange={handlePlatformChange}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All platforms" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All platforms</SelectItem>
              {platforms.map((p) => (
                <SelectItem key={p.name} value={p.name}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button aria-label="Compose">
                <PenLine className="size-4" />
                <span className="hidden sm:inline">Compose</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => router.visit(newStoryPath())}>
                Short form
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() =>
                  router.visit(newStoryPath({ type: story_types.long_form }))
                }
              >
                Long form
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Drafts */}
        {drafts.length > 0 && (
          <section>
            <h2 className="text-muted-foreground mb-4 border-b pb-2 text-xs font-semibold tracking-widest uppercase">
              Drafts
            </h2>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {drafts.map((story) => (
                <StoryCard
                  key={story.id}
                  story={story}
                  platformLabels={platformLabels}
                />
              ))}
            </div>
          </section>
        )}

        {/* Scheduled */}
        {scheduled.length > 0 && (
          <section>
            <h2 className="text-muted-foreground mb-4 border-b pb-2 text-xs font-semibold tracking-widest uppercase">
              Scheduled
            </h2>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {scheduled.map((story) => (
                <StoryCard
                  key={story.id}
                  story={story}
                  platformLabels={platformLabels}
                />
              ))}
            </div>
          </section>
        )}

        {/* Published */}
        {published.length > 0 && (
          <section>
            <h2 className="text-muted-foreground mb-4 border-b pb-2 text-xs font-semibold tracking-widest uppercase">
              Published
            </h2>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {published.map((story) => (
                <StoryCard
                  key={story.id}
                  story={story}
                  platformLabels={platformLabels}
                />
              ))}
            </div>
          </section>
        )}

        {filteredStories.length === 0 && (
          <div className="text-muted-foreground py-20 text-center">
            {selectedPlatform ? (
              <>
                <p className="font-serif text-lg">
                  No stories for {platformLabels[selectedPlatform]}.
                </p>
                <p className="mt-1 text-sm">
                  Try selecting a different platform or view all.
                </p>
              </>
            ) : (
              <>
                <p className="font-serif text-lg">The presses are quiet.</p>
                <p className="mt-1 text-sm">
                  Compose your first story to get started.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
